import { AudioInputDevice } from "./types";

type DeviceChangeListener = () => void;

/**
 * Manages audio input devices and microphone access.
 *
 * Handles:
 * - Device enumeration
 * - Permission requests
 * - Device change events
 */
export class AudioInputManager {
  private listeners: Set<DeviceChangeListener> = new Set();
  private boundHandleDeviceChange: () => void;

  constructor() {
    this.boundHandleDeviceChange = this.handleDeviceChange.bind(this);

    // Listen for device changes (new mic plugged in, etc.)
    if (typeof navigator !== "undefined" && navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener("devicechange", this.boundHandleDeviceChange);
    }
  }

  /**
   * Request microphone permission by triggering getUserMedia.
   * This will show the browser's permission prompt if not already granted.
   * @throws Error if mediaDevices API is not available or permission is denied
   */
  async requestPermission(): Promise<void> {
    if (typeof navigator === "undefined" || !navigator.mediaDevices) {
      throw new Error("Media devices not available. Recording requires HTTPS or localhost.");
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the stream immediately - we just wanted to trigger permission
      stream.getTracks().forEach(track => track.stop());
    } catch (error) {
      if (error instanceof DOMException) {
        if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
          throw new Error("Microphone permission denied. Please allow access in your browser settings.");
        }
        if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
          throw new Error("No microphone found. Please connect a microphone and try again.");
        }
        if (error.name === "NotReadableError" || error.name === "TrackStartError") {
          throw new Error("Microphone is in use by another application.");
        }
      }
      throw error;
    }
  }

  /**
   * Check if microphone permission has been granted.
   * Note: This uses the Permissions API which may not be available in all browsers.
   * Returns false if mediaDevices API is not available.
   */
  async hasPermission(): Promise<boolean> {
    if (typeof navigator === "undefined" || !navigator.mediaDevices) {
      return false;
    }

    try {
      // Try Permissions API first (Chrome, Edge)
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: "microphone" as PermissionName });
        return result.state === "granted";
      }
    } catch {
      // Permissions API not supported or microphone not queryable
    }

    // Fallback: try to enumerate devices - if we get labels, we have permission
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(d => d.kind === "audioinput");
      // If we have labels, permission was granted
      return audioInputs.some(d => d.label !== "");
    } catch {
      return false;
    }
  }

  /**
   * Get list of available audio input devices.
   * Device labels will only be available after permission is granted.
   * Returns empty array if mediaDevices API is not available.
   */
  async getDevices(): Promise<AudioInputDevice[]> {
    if (typeof navigator === "undefined" || !navigator.mediaDevices) {
      return [];
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      return devices
        .filter(device => device.kind === "audioinput")
        .map((device, index) => ({
          deviceId: device.deviceId,
          // Provide a fallback label if permission not granted
          label: device.label || `Microphone ${index + 1}`,
        }));
    } catch {
      return [];
    }
  }

  /**
   * Subscribe to device change events.
   * Returns an unsubscribe function.
   */
  onDeviceChange(listener: DeviceChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private handleDeviceChange(): void {
    this.listeners.forEach(listener => listener());
  }

  /**
   * Clean up resources.
   */
  destroy(): void {
    this.listeners.clear();

    if (typeof navigator !== "undefined" && navigator.mediaDevices) {
      navigator.mediaDevices.removeEventListener("devicechange", this.boundHandleDeviceChange);
    }
  }
}
